import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AvailabilityDay, ReservationCreated, SeatingArea } from '@sazerac/design-tokens';
import { useAvailability, useCreateReservation, useJoinWaitlist, useSite } from '../api/queries';
import { ApiError, apiUrl } from '../api/client';
import { usePageMeta } from '../hooks/usePageMeta';
import { Field, FormMessage, StepPips } from '../components/ui';
import { addDays, formatLongDate, formatMoneyExact, todayInZone } from '../lib/format';
import { storeManageToken } from '../lib/guestToken';

/**
 * Six-step table reservation flow, matching the approved design:
 *   1 Location, date, party size
 *   2 Time (server-computed availability)
 *   3 Seating preference
 *   4 Guest details and occasion
 *   5 Review (and optional deposit)
 *   6 Confirmation
 *
 * Availability is never computed in the browser — every slot shown comes from the API,
 * and the API re-validates it inside a transaction when the booking is submitted, so a
 * slot taken in the meantime is rejected rather than double-booked.
 */

const OCCASIONS = [
  'Casual Visit',
  'Birthday',
  'Anniversary',
  'Business Meeting',
  'Date Night',
  'Private Celebration',
];

interface FormState {
  locationId: string;
  date: string;
  partySize: number;
  time: string;
  seatingAreaId: string;
  guestName: string;
  email: string;
  phone: string;
  occasion: string;
  smokingPreference: 'NonSmoking' | 'SmokingArea';
  specialRequests: string;
  acceptedTerms: boolean;
  requestDeposit: boolean;
  marketingConsent: boolean;
}

export function ReservationsPage() {
  const { data: site } = useSite();
  const navigate = useNavigate();
  const createReservation = useCreateReservation();
  const joinWaitlist = useJoinWaitlist();

  const locations = site?.locations ?? [];
  const primary = locations[0];
  const timeZone = primary?.timeZoneId ?? 'Europe/Rome';

  const [step, setStep] = useState(1);
  const [confirmation, setConfirmation] = useState<ReservationCreated | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [waitlisted, setWaitlisted] = useState(false);

  const [form, setForm] = useState<FormState>({
    locationId: '',
    date: '',
    partySize: 2,
    time: '',
    seatingAreaId: '',
    guestName: '',
    email: '',
    phone: '',
    occasion: OCCASIONS[0],
    smokingPreference: 'NonSmoking',
    specialRequests: '',
    acceptedTerms: false,
    requestDeposit: false,
    marketingConsent: false,
  });

  usePageMeta({
    title: 'Reservations',
    description: 'Reserve a table at SAZERAC. Choose your date, time, party size and seating preference.',
  });

  // Default to the first venue and today's date in that venue's time zone.
  useEffect(() => {
    if (!primary || form.locationId) return;
    setForm((current) => ({
      ...current,
      locationId: primary.id,
      date: current.date || todayInZone(primary.timeZoneId),
    }));
  }, [primary, form.locationId]);

  const availabilityQuery = useAvailability(
    form.locationId || undefined,
    form.date,
    form.partySize,
    form.seatingAreaId || null,
    step >= 2,
  );
  const availability = availabilityQuery.data;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key as string]) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  };

  const seatingAreas: SeatingArea[] = availability?.seatingAreas ?? primary?.seatingAreas ?? [];
  const maxParty = availability?.maxPartySize ?? 8;
  const minParty = availability?.minPartySize ?? 1;

  const partySizes = useMemo(
    () => Array.from({ length: maxParty - minParty + 1 }, (_, index) => minParty + index),
    [minParty, maxParty],
  );

  if (site && !site.reservationsEnabled) {
    return (
      <div className="sz-page sz-page--narrow">
        <div className="sz-page-head">
          <p className="sz-eyebrow">Book a Table</p>
          <h1>Reservations</h1>
        </div>
        <div className="sz-notice sz-notice--warning" role="status">
          Online reservations are temporarily unavailable. Please call the venue
          {primary?.phone ? ` on ${primary.phone}` : ''} and we will take your booking directly.
        </div>
      </div>
    );
  }

  // ---- step validation -----------------------------------------------------

  const validateStep4 = () => {
    const next: Record<string, string> = {};
    if (!form.guestName.trim()) next.guestName = 'Please tell us your name.';
    if (!form.email.trim() || !form.email.includes('@')) next.email = 'Please enter a valid email address.';
    if (!form.phone.trim()) next.phone = 'A mobile number lets us reach you about your table.';
    if (!form.acceptedTerms) next.acceptedTerms = 'Please accept the reservation terms and cancellation policy.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    try {
      const result = await createReservation.mutateAsync({
        locationId: form.locationId,
        date: form.date,
        time: form.time,
        partySize: form.partySize,
        preferredSeatingAreaId: form.seatingAreaId || null,
        smokingPreference: form.smokingPreference,
        guestName: form.guestName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        occasion: form.occasion,
        specialRequests: form.specialRequests.trim() || null,
        acceptedTerms: form.acceptedTerms,
        requestDeposit: form.requestDeposit,
        marketingConsent: form.marketingConsent,
      });
      // Keep the manage token for this tab so the guest can jump straight to their
      // booking without waiting for the email.
      storeManageToken(result.manageToken);
      setConfirmation(result);
      setStep(6);
    } catch (error) {
      // A slot taken in the meantime sends the guest back to pick another time.
      if (error instanceof ApiError && error.code === 'slot_unavailable') {
        setStep(2);
        void availabilityQuery.refetch();
      }
    }
  };

  const onWaitlist = async () => {
    if (!form.guestName.trim() || !form.email.trim()) {
      setStep(4);
      setErrors({ guestName: 'We need your details to add you to the waiting list.' });
      return;
    }
    await joinWaitlist.mutateAsync({
      locationId: form.locationId,
      date: form.date,
      time: form.time || '20:00',
      partySize: form.partySize,
      guestName: form.guestName,
      email: form.email,
      phone: form.phone,
    });
    setWaitlisted(true);
  };

  return (
    <div className="sz-page sz-page--narrow">
      <div className="sz-page-head">
        <p className="sz-eyebrow">Book a Table</p>
        <h1>Reservations</h1>
        <Link to="/manage-reservation" className="sz-link-caps" style={{ display: 'inline-block', marginTop: 14 }}>
          Manage an existing reservation →
        </Link>
      </div>

      <StepPips total={6} current={step} />

      {step === 1 && (
        <div className="sz-form-stack">
          <p className="sz-step-label">Step 1 · Location, Date &amp; Party Size</p>

          <Field label="Location" htmlFor="res-location">
            <select
              id="res-location"
              className="sz-select"
              value={form.locationId}
              onChange={(event) => set('locationId', event.target.value)}
            >
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name} — {location.addressLine1}
                </option>
              ))}
            </select>
          </Field>

          <div className="sz-form-grid">
            <Field label="Date" htmlFor="res-date" error={errors.date}>
              <input
                id="res-date"
                type="date"
                className="sz-input"
                value={form.date}
                min={todayInZone(timeZone)}
                max={addDays(todayInZone(timeZone), 365)}
                onChange={(event) => set('date', event.target.value)}
              />
            </Field>

            <Field
              label="Party Size"
              htmlFor="res-party"
              hint={`Parties above ${maxParty} are arranged by our events team.`}
            >
              <select
                id="res-party"
                className="sz-select"
                value={form.partySize}
                onChange={(event) => set('partySize', Number(event.target.value))}
              >
                {partySizes.map((size) => (
                  <option key={size} value={size}>
                    {size} {size === 1 ? 'Guest' : 'Guests'}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <button
            type="button"
            className="sz-btn sz-btn--gold"
            disabled={!form.locationId || !form.date}
            onClick={() => setStep(2)}
          >
            View Available Times
          </button>

          <p className="sz-field-hint">
            Larger parties? <Link to="/events">Send an events enquiry</Link> and we will plan the room with you.
          </p>
        </div>
      )}

      {step === 2 && (
        <TimeStep
          availability={availability}
          isLoading={availabilityQuery.isLoading}
          isError={availabilityQuery.isError}
          error={availabilityQuery.error}
          selected={form.time}
          date={form.date}
          onSelect={(time) => set('time', time)}
          onBack={() => setStep(1)}
          onContinue={() => setStep(3)}
          onWaitlist={onWaitlist}
          waitlisted={waitlisted || joinWaitlist.isSuccess}
          waitlistPending={joinWaitlist.isPending}
        />
      )}

      {step === 3 && (
        <div>
          <p className="sz-step-label">Step 3 · Seating Preference</p>

          <div className="sz-seating-grid">
            <button
              type="button"
              className="sz-seating-option"
              data-active={form.seatingAreaId === ''}
              onClick={() => set('seatingAreaId', '')}
            >
              <span className="sz-seating-option__name">No preference</span>
              <span className="sz-seating-option__note">Seat us wherever suits</span>
            </button>

            {seatingAreas.map((area) => (
              <button
                key={area.id}
                type="button"
                className="sz-seating-option"
                data-active={form.seatingAreaId === area.id}
                onClick={() => set('seatingAreaId', area.id)}
              >
                <span className="sz-seating-option__name">{area.name}</span>
                {area.description && <span className="sz-seating-option__note">{area.description}</span>}
              </button>
            ))}
          </div>

          {/* The design offers a preference; it must never read as a guaranteed table. */}
          <p className="sz-field-hint" style={{ marginBottom: 28 }}>
            A seating preference is a request, not a guaranteed table assignment. We allocate tables on
            the day and will always try to honour it.
          </p>

          <div className="sz-form-actions">
            <button type="button" className="sz-btn sz-btn--ghost sz-btn--flex" onClick={() => setStep(2)}>
              Back
            </button>
            <button type="button" className="sz-btn sz-btn--gold sz-btn--flex" onClick={() => setStep(4)}>
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="sz-form-stack">
          <p className="sz-step-label">Step 4 · Guest Details &amp; Occasion</p>

          <Field label="Full Name" htmlFor="res-name" error={errors.guestName}>
            <input
              id="res-name"
              className="sz-input"
              autoComplete="name"
              value={form.guestName}
              aria-invalid={Boolean(errors.guestName)}
              onChange={(event) => set('guestName', event.target.value)}
            />
          </Field>

          <div className="sz-form-grid">
            <Field label="Mobile Number" htmlFor="res-phone" error={errors.phone}>
              <input
                id="res-phone"
                className="sz-input"
                type="tel"
                autoComplete="tel"
                value={form.phone}
                aria-invalid={Boolean(errors.phone)}
                onChange={(event) => set('phone', event.target.value)}
              />
            </Field>

            <Field label="Email" htmlFor="res-email" error={errors.email}>
              <input
                id="res-email"
                className="sz-input"
                type="email"
                autoComplete="email"
                value={form.email}
                aria-invalid={Boolean(errors.email)}
                onChange={(event) => set('email', event.target.value)}
              />
            </Field>
          </div>

          <Field label="Occasion" htmlFor="res-occasion">
            <select
              id="res-occasion"
              className="sz-select"
              value={form.occasion}
              onChange={(event) => set('occasion', event.target.value)}
            >
              {OCCASIONS.map((occasion) => (
                <option key={occasion} value={occasion}>
                  {occasion}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Smoking Preference" htmlFor="res-smoking">
            <select
              id="res-smoking"
              className="sz-select"
              value={form.smokingPreference}
              onChange={(event) => set('smokingPreference', event.target.value as FormState['smokingPreference'])}
            >
              <option value="NonSmoking">Non-Smoking</option>
              <option value="SmokingArea">Smoking Area</option>
            </select>
          </Field>

          <Field
            label="Special Requests"
            htmlFor="res-notes"
            hint="Allergies, accessibility needs, celebrations — anything that helps us prepare."
          >
            <textarea
              id="res-notes"
              className="sz-textarea"
              rows={3}
              value={form.specialRequests}
              onChange={(event) => set('specialRequests', event.target.value)}
            />
          </Field>

          <div>
            <label className="sz-checkbox">
              <input
                type="checkbox"
                checked={form.acceptedTerms}
                aria-invalid={Boolean(errors.acceptedTerms)}
                onChange={(event) => set('acceptedTerms', event.target.checked)}
              />
              <span>
                I accept the <Link to="/policies/reservation-terms">reservation terms</Link> and{' '}
                <Link to="/policies/cancellation-policy">cancellation policy</Link>.
              </span>
            </label>
            {errors.acceptedTerms && (
              <p className="sz-field-error" role="alert">
                {errors.acceptedTerms}
              </p>
            )}
          </div>

          <label className="sz-checkbox">
            <input
              type="checkbox"
              checked={form.marketingConsent}
              onChange={(event) => set('marketingConsent', event.target.checked)}
            />
            <span>
              Send me occasional news, events and seasonal menus. (Optional — your booking
              confirmation is sent either way.)
            </span>
          </label>

          <div className="sz-form-actions">
            <button type="button" className="sz-btn sz-btn--ghost sz-btn--flex" onClick={() => setStep(3)}>
              Back
            </button>
            <button
              type="button"
              className="sz-btn sz-btn--gold sz-btn--flex"
              onClick={() => {
                if (validateStep4()) setStep(5);
              }}
            >
              Review Reservation
            </button>
          </div>
        </div>
      )}

      {step === 5 && (
        <div>
          <p className="sz-step-label">Step 5 · Review{availability?.depositEnabled ? ' & Deposit' : ''}</p>

          <div className="sz-panel sz-summary">
            <p>
              {primary?.name} · Table for {form.partySize} · {form.guestName}
            </p>
            <p>
              {formatLongDate(form.date)} at {form.time} · Seating:{' '}
              {seatingAreas.find((area) => area.id === form.seatingAreaId)?.name ?? 'No preference'}
            </p>
            <p>Occasion: {form.occasion}</p>
            {form.specialRequests && <p>Requests: {form.specialRequests}</p>}
          </div>

          {availability?.depositEnabled && availability.depositPerPerson > 0 && (
            <div className="sz-panel" style={{ marginBottom: 28 }}>
              <label className="sz-checkbox" style={{ color: 'var(--sz-white)', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.requestDeposit}
                  onChange={(event) => set('requestDeposit', event.target.checked)}
                />
                <span>
                  Secure with a {formatMoneyExact(availability.depositPerPerson, availability.currency)}{' '}
                  per-person deposit (optional) —{' '}
                  {formatMoneyExact(availability.depositPerPerson * form.partySize, availability.currency)} total
                </span>
              </label>
              <p className="sz-field-hint" style={{ marginTop: 10 }}>
                Deposits are redeemed against your bill and refunded if you cancel before the cut-off.
                No card details are collected by this website.
              </p>
            </div>
          )}

          <FormMessage error={createReservation.error} />

          <div className="sz-form-actions" style={{ marginTop: 20 }}>
            <button type="button" className="sz-btn sz-btn--ghost sz-btn--flex" onClick={() => setStep(4)}>
              Edit
            </button>
            <button
              type="button"
              className="sz-btn sz-btn--gold sz-btn--flex"
              disabled={createReservation.isPending}
              onClick={() => void submit()}
            >
              {createReservation.isPending ? 'Confirming…' : 'Confirm Reservation'}
            </button>
          </div>
        </div>
      )}

      {step === 6 && confirmation && (
        <ConfirmationStep
          confirmation={confirmation}
          onManage={() => navigate(`/manage-reservation?token=${encodeURIComponent(confirmation.manageToken)}`)}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ STEP TWO

function TimeStep({
  availability,
  isLoading,
  isError,
  error,
  selected,
  date,
  onSelect,
  onBack,
  onContinue,
  onWaitlist,
  waitlisted,
  waitlistPending,
}: {
  availability?: AvailabilityDay;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  selected: string;
  date: string;
  onSelect: (time: string) => void;
  onBack: () => void;
  onContinue: () => void;
  onWaitlist: () => void;
  waitlisted: boolean;
  waitlistPending: boolean;
}) {
  const slots = availability?.slots ?? [];
  const anyAvailable = slots.some((slot) => slot.isAvailable);

  return (
    <div>
      <p className="sz-step-label">Step 2 · Select a Time</p>
      <p className="sz-field-hint" style={{ marginTop: -12, marginBottom: 20 }}>
        {formatLongDate(date)}
        {availability?.durationMinutes ? ` · tables are held for ${availability.durationMinutes} minutes` : ''}
      </p>

      {isLoading && (
        <div className="sz-slot-grid" aria-hidden="true">
          {Array.from({ length: 12 }).map((_, index) => (
            <div key={index} className="sz-skeleton" style={{ height: 44 }} />
          ))}
        </div>
      )}

      {isError && <FormMessage error={error} />}

      {!isLoading && !isError && availability && !availability.isOpen && (
        <div className="sz-notice" role="status">
          {availability.unavailableMessage ?? 'We are closed on this date.'} Please choose another date.
        </div>
      )}

      {!isLoading && availability?.isOpen && (
        <>
          <div className="sz-slot-grid" role="group" aria-label="Available times">
            {slots.map((slot) => (
              <button
                key={slot.time}
                type="button"
                className="sz-slot"
                data-selected={selected === slot.time}
                disabled={!slot.isAvailable}
                aria-label={
                  slot.isAvailable
                    ? `${slot.time}, ${slot.tablesRemaining} table${slot.tablesRemaining === 1 ? '' : 's'} available`
                    : `${slot.time}, unavailable`
                }
                onClick={() => onSelect(slot.time)}
              >
                {slot.time}
              </button>
            ))}
          </div>

          {selected && slots.find((slot) => slot.time === selected)?.preferredAreaFull && (
            <div className="sz-notice" style={{ marginTop: 20 }} role="status">
              Your preferred seating area is fully booked at {selected}, but we do have a table free at
              that time in another area.
            </div>
          )}

          {!anyAvailable && (
            <div className="sz-panel" style={{ marginTop: 24 }}>
              <p style={{ fontSize: 14, color: 'var(--sz-white)', margin: '0 0 10px' }}>
                No tables available on this date.
              </p>
              <p style={{ fontSize: 13, color: 'var(--sz-sand)', margin: '0 0 16px' }}>
                Try another date, or join the waiting list and we will contact you if a table opens.
              </p>
              {availability.waitlistAvailable &&
                (waitlisted ? (
                  <p style={{ color: 'var(--sz-gold)', fontSize: 13, margin: 0 }}>
                    You are on the waiting list. This is not a confirmed reservation — we will be in touch.
                  </p>
                ) : (
                  <button
                    type="button"
                    className="sz-btn sz-btn--ghost"
                    onClick={onWaitlist}
                    disabled={waitlistPending}
                  >
                    {waitlistPending ? 'Adding you…' : 'Join Waiting List'}
                  </button>
                ))}
            </div>
          )}
        </>
      )}

      <div className="sz-form-actions" style={{ marginTop: 28 }}>
        <button type="button" className="sz-btn sz-btn--ghost sz-btn--flex" onClick={onBack}>
          Back
        </button>
        <button type="button" className="sz-btn sz-btn--gold sz-btn--flex" disabled={!selected} onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- STEP SIX

function ConfirmationStep({
  confirmation,
  onManage,
}: {
  confirmation: ReservationCreated;
  onManage: () => void;
}) {
  const calendarHref = apiUrl('/api/public/reservations/me/calendar.ics', {
    token: confirmation.manageToken,
  });

  return (
    <div className="sz-panel sz-confirmation" role="status">
      <p className="sz-eyebrow sz-eyebrow--tight">
        {confirmation.requiresStaffApproval ? 'Reservation Requested' : 'Reservation Confirmed'}
      </p>
      <h2 className="sz-confirmation__ref">Reference #{confirmation.reference}</h2>

      <p className="sz-confirmation__detail">
        {formatLongDate(confirmation.date)} at {confirmation.time} · Table for {confirmation.partySize} ·{' '}
        {confirmation.locationName}
      </p>

      <p className="sz-confirmation__note">
        {confirmation.requiresStaffApproval
          ? 'Our team will review and confirm your table shortly. You will receive an email once it is confirmed.'
          : 'A confirmation has been sent to your email address.'}
      </p>

      {confirmation.notices.map((notice) => (
        <p key={notice} className="sz-confirmation__notice">
          {notice}
        </p>
      ))}

      {confirmation.deposit?.simulated && (
        <p className="sz-confirmation__notice">{confirmation.deposit.message}</p>
      )}

      <div className="sz-confirmation__actions">
        <a href={calendarHref} className="sz-btn sz-btn--ghost" download="sazerac-reservation.ics">
          Add to Calendar
        </a>
        <button type="button" className="sz-btn sz-btn--ghost" onClick={onManage}>
          Manage Reservation
        </button>
        <Link to="/" className="sz-btn sz-btn--gold">
          Return Home
        </Link>
      </div>

      <p className="sz-confirmation__small">
        Your management link expires shortly for your security. You can request a new one at any time
        from the Manage Reservation page.
      </p>
    </div>
  );
}
